import { collect, iterate } from "../../graph/paging.ts";
import { MODULE_SCOPES } from "../../auth/config.ts";
import { InteractionRequiredError, NotSignedInError } from "../../auth/broker.ts";
import { GraphError } from "../../graph/client.ts";
import { writeFile, WriteFailedError } from "../../storage/fsa.ts";
import { getCursor, putCursor, isItemDone, itemNeedsExport, putItem } from "../../storage/idb.ts";
import type { RunLogger } from "../../export/run.ts";
import { ExportAbortedError, throwIfAborted } from "../../export/abort.ts";
import {
    withinRange,
    isContainerSelected,
    walkScope,
    scopeCovers,
    type DateRange,
    type ContainerSelection
} from "../../export/plan.ts";
import { chatKey, chatLabel, messagePath } from "./paths.ts";
import { chatUnchangedSinceCompletion } from "./chat-freshness.ts";
import { fetchHostedImages } from "./attachments.ts";
import { sanitizeMessageBody } from "./sanitize-html.ts";
import { renderChatIndex, renderTeamsIndex, type ChatIndexSummary } from "./render.ts";
import type { StoredMessage } from "./stored-message.ts";
import type { Chat, ChatMessage } from "./types.ts";

/**
 * Teams export (ARCHITECTURE.md §15 step 4).
 *
 * Chosen as the first module specifically to prove the full pipeline
 * including resume (§15): incremental consent, per-chat paging cursors,
 * per-message resumable items, hosted-content assets as their own resumable
 * items, and an idempotent local render pass that never depends on anything
 * held in memory.
 */

const SCOPES = MODULE_SCOPES.teams;

export interface TeamsProgress {
    chatsTotal: number;
    chatsDone: number;
    currentChatLabel?: string;
    /** Bytes written for the conversation in progress. */
    currentChatBytes: number;
    /** Bytes written across this whole invocation. */
    totalBytes: number;
}

export type TeamsProgressCallback = (progress: TeamsProgress) => void;

/** One finished conversation, for the console's scrollback. */
export interface TeamsChatSummary {
    label: string;
    /** Messages fetched and written *this call* — 0 if nothing was new. */
    messages: number;
    bytes: number;
    /**
     * True when this chat had already been fully exported before this run
     * began. Independent of `messages`: a chat can be alreadyComplete and
     * still have messages > 0, when Graph reports it changed since and the
     * re-check found new activity to fetch.
     */
    alreadyComplete: boolean;
}

export interface ExportTeamsParams {
    runId: string;
    root: FileSystemDirectoryHandle;
    /** AccountInfo.localAccountId, best-effort, for labeling 1:1 chats by the other party. */
    selfUserId: string | undefined;
    /** From the Scan step: only export these chats (undefined/"all" = every chat). */
    selection?: ContainerSelection;
    /** From the Scan step: skip messages outside this date window. */
    dateRange?: DateRange;
    onProgress?: TeamsProgressCallback;
    onChatComplete?: (summary: TeamsChatSummary) => void;
    /** Aborts at the next clean checkpoint. */
    signal?: AbortSignal;
    logger: RunLogger;
}

/**
 * Error text that is safe for export.log (§14). A WriteFailedError's own
 * message carries the destination path — and a Teams path carries the chat's
 * label (its topic or participants' names) — so only the underlying browser
 * error, which never quotes the path, is logged.
 */
function logSafeError(err: unknown): string {
    if (err instanceof GraphError) {
        return err.toLogString();
    }
    if (err instanceof WriteFailedError) {
        const cause = err.cause;
        return `write failed: ${cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)}`;
    }
    return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
}

function attachmentNameMap(message: ChatMessage): Map<string, string> {
    const map = new Map<string, string>();
    for (const attachment of message.attachments ?? []) {
        if (attachment.name) {
            map.set(attachment.id, attachment.name);
        }
    }
    return map;
}

/**
 * Fetches and writes every not-yet-done message in one chat, checkpointing the
 * paging cursor at each page boundary (see paging.ts's onNextLink) and each
 * message as its own item (see idb.ts's items store) — an interruption at any
 * point costs at most the one message in flight.
 *
 * Chat messages have no delta endpoint (§8), so a chat marked done does not
 * simply stop forever — that would silently miss anything sent after the
 * export finished. Instead, `chat.lastUpdatedDateTime` (bumped by Graph on any
 * new activity) is compared against the timestamp recorded when the chat was
 * last confirmed complete. If Graph itself says nothing changed, the chat is
 * skipped with zero network calls; otherwise it gets a fresh listing pass,
 * with itemNeedsExport() making that pass cheap — already-known, unchanged
 * messages are skipped instantly, so only new or edited ones cost a write.
 */
async function exportChatMessages(params: {
    runId: string;
    root: FileSystemDirectoryHandle;
    chat: Chat;
    key: string;
    dateRange?: DateRange;
    logger: RunLogger;
    signal?: AbortSignal;
    onBytes?: (bytes: number) => void;
}): Promise<{ messages: number; bytes: number; alreadyComplete: boolean }> {
    const { runId, root, chat, key, dateRange, logger, signal, onBytes } = params;

    const scope = walkScope(dateRange);
    const cursor = await getCursor(runId, "teams", chat.id);
    const alreadyComplete = cursor?.done ?? false;
    // Messages outside an earlier, narrower date window were skipped without
    // being marked done, so neither "unchanged since completion" nor a stored
    // nextLink can be trusted for a wider window: the chat is listed again
    // from page 1 (messages already exported are still skipped).
    const covered = scopeCovers(cursor?.scope, scope);

    if (alreadyComplete && covered) {
        const unchanged = chatUnchangedSinceCompletion(
            chat.lastUpdatedDateTime,
            cursor?.completedAtSourceUpdate
        );

        if (unchanged) {
            return { messages: 0, bytes: 0, alreadyComplete: true };
        }
        // Either Graph reports real activity since we finished, or one of the
        // timestamps is missing and the comparison can't be trusted — in both
        // cases a re-scan is safer than assuming nothing changed. Falls
        // through to the same walk below; itemNeedsExport() keeps it cheap.
    }

    // A done chat always restarts its listing from page 1 rather than reusing
    // any stored nextLink — that link was consumed when the original walk
    // finished. An interrupted re-scan is simply retried from page 1 next
    // time too (see the onNextLink note below), which costs an extra listing
    // pass but nothing already written is redone.
    const startUrl =
        !alreadyComplete && covered && cursor?.nextLink
            ? cursor.nextLink
            : `/chats/${chat.id}/messages?$top=50`;

    let messages = 0;
    let bytes = 0;

    for await (const message of iterate<ChatMessage>(startUrl, {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {}),
        onNextLink: (nextLink) => {
            // Only checkpointed for the original, not-yet-complete walk. A
            // re-scan pass over an already-complete chat does not persist a
            // mid-walk position — see the comment on startUrl above.
            if (!alreadyComplete) {
                void putCursor({ runId, module: "teams", subKey: chat.id, nextLink, done: false, scope });
            }
        }
    })) {
        // Checked per message rather than only per chat: a chat with thousands
        // of messages would otherwise ignore a pause until it finished.
        throwIfAborted(signal);

        const itemId = `${chat.id}:${message.id}`;
        // Graph bumps lastModifiedDateTime on an edit, a reaction or a
        // deletion, so an edited message is written again with its new text.
        const version = message.lastModifiedDateTime ?? message.createdDateTime;
        if (!(await itemNeedsExport(runId, "teams", itemId, version))) {
            continue;
        }

        // Date filter (Scan step): skip out-of-window messages without marking
        // them done, so a later run with a wider window can still pick them up.
        if (dateRange && !withinRange(message.createdDateTime, dateRange)) {
            continue;
        }

        if (message.deletedDateTime) {
            // Deleted after an earlier run exported it: keep that copy and its
            // record — this is a backup, and preserving what was captured
            // outranks mirroring a later deletion (same as Mail and OneDrive).
            if (await isItemDone(runId, "teams", itemId)) {
                continue;
            }
            // Respect the deletion rather than exporting content the sender
            // or recipient removed. Recorded as done so it is not retried.
            await putItem({ runId, module: "teams", itemId, status: "skipped", bytes: 0 });
            continue;
        }

        const assets = await fetchHostedImages({
            runId,
            chatId: chat.id,
            messageId: message.id,
            chatKey: key,
            bodyHtml: message.body.content,
            root,
            scopes: SCOPES,
            logger
        });
        const urlMap = assets.urlMap;

        const html = sanitizeMessageBody(message.body.content, message.body.contentType, {
            assetUrlMap: urlMap,
            attachmentNames: attachmentNameMap(message)
        });

        const stored: StoredMessage = {
            id: message.id,
            createdDateTime: message.createdDateTime,
            from: message.from?.user?.displayName ?? "Unknown",
            html
        };

        const written = await writeFile(root, messagePath(key, message.id), JSON.stringify(stored));
        await putItem({ runId, module: "teams", itemId, status: "done", bytes: written, version });

        messages += 1;
        // Counts the message JSON plus any images fetched for it, so the
        // reported total matches what actually landed on disk.
        const chunk = written + assets.bytes;
        bytes += chunk;
        onBytes?.(chunk);
    }

    // Reached only if the loop above completed without throwing — every page
    // was fetched and every message in it resolved to either "done" or
    // "skipped". A throw partway through leaves cursor.done untouched (false
    // for a first pass, still true for an interrupted re-scan — see above),
    // which is exactly what a resume needs to pick the chat back up.
    await putCursor({
        runId,
        module: "teams",
        subKey: chat.id,
        done: true,
        scope,
        ...(chat.lastUpdatedDateTime ? { completedAtSourceUpdate: chat.lastUpdatedDateTime } : {})
    });
    return { messages, bytes, alreadyComplete };
}

export interface TeamsResult {
    chatsTotal: number;
    chatsExported: number;
    messages: number;
    bytes: number;
    errors: number;
}

export async function exportTeams(params: ExportTeamsParams): Promise<TeamsResult> {
    const { runId, root, selfUserId, selection, dateRange, onProgress, onChatComplete, signal, logger } =
        params;

    await logger.info("listing chats");
    const allChats = await collect<Chat>("/me/chats?$expand=members&$top=50", {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {})
    });
    // Scan-step selection: only the chosen conversations.
    const chats = allChats.filter((c) => isContainerSelected(selection, c.id));
    await logger.info(`found ${allChats.length} chats, ${chats.length} selected`);

    let totalBytes = 0;
    let totalMessages = 0;
    let chatsExported = 0;
    let errors = 0;
    const chatSummaries: ChatIndexSummary[] = [];

    for (let i = 0; i < chats.length; i += 1) {
        throwIfAborted(signal);

        const chat = chats[i];
        if (!chat) {
            continue;
        }

        const label = chatLabel(chat, selfUserId);
        let chatBytes = 0;

        onProgress?.({
            chatsTotal: chats.length,
            chatsDone: i,
            currentChatLabel: label,
            currentChatBytes: 0,
            totalBytes
        });

        const key = await chatKey(chat, selfUserId);

        try {
            const result = await exportChatMessages({
                runId,
                root,
                chat,
                key,
                ...(dateRange ? { dateRange } : {}),
                logger,
                ...(signal ? { signal } : {}),
                onBytes: (chunk) => {
                    chatBytes += chunk;
                    totalBytes += chunk;
                    onProgress?.({
                        chatsTotal: chats.length,
                        chatsDone: i,
                        currentChatLabel: label,
                        currentChatBytes: chatBytes,
                        totalBytes
                    });
                }
            });

            totalMessages += result.messages;
            // Counts as "worked on" whether this was the chat's first export
            // or a re-check of an already-complete one that turned up new
            // messages — not when it was confirmed unchanged and skipped.
            if (!result.alreadyComplete || result.messages > 0) {
                chatsExported += 1;
            }
            onChatComplete?.({
                label,
                messages: result.messages,
                bytes: result.bytes,
                alreadyComplete: result.alreadyComplete
            });
        } catch (err) {
            // A pause/stop is the user's decision, not a per-chat failure —
            // let it out so the whole run stops here with checkpoints intact.
            if (err instanceof ExportAbortedError) {
                throw err;
            }
            // Auth expiry affects every subsequent Graph call, not just this
            // chat — halting immediately avoids burning through the rest of
            // the list generating the same failure per chat. Anything else
            // (a chat the user lost access to mid-run, a transient error the
            // client's own retries already exhausted) is this chat's problem
            // alone: log it, leave the cursor unmarked so a future resume
            // retries it, and keep going.
            if (err instanceof InteractionRequiredError || err instanceof NotSignedInError) {
                throw err;
            }
            errors += 1;
            await logger.error(`chat failed, will retry on resume: ${logSafeError(err)}`);
            onChatComplete?.({ label, messages: 0, bytes: 0, alreadyComplete: false });
        }

        // Idempotent and network-free — safe whether this chat just finished,
        // was already complete on a resume, or failed above and is only
        // partially fetched so far.
        //
        // Guarded with the same rules as the fetch above. It used to sit
        // outside any try, so one chat whose transcript could not be rendered
        // (e.g. a folder name Chromium rejected) threw out of exportTeams and
        // took every chat after it down with it. Rendering runs again on every
        // run, so a chat skipped here is retried next time.
        try {
            chatSummaries.push(await renderChatIndex(root, key, chat, label));
        } catch (err) {
            if (
                err instanceof ExportAbortedError ||
                err instanceof InteractionRequiredError ||
                err instanceof NotSignedInError
            ) {
                throw err;
            }
            errors += 1;
            await logger.error(`chat transcript render failed, will retry on resume: ${logSafeError(err)}`);
        }
    }

    // Idempotent and network-free, like each chat's own index.html above —
    // rebuilt every run from the summaries just produced, never from
    // anything held over from an earlier invocation.
    await renderTeamsIndex(root, chatSummaries);

    onProgress?.({
        chatsTotal: chats.length,
        chatsDone: chats.length,
        currentChatBytes: 0,
        totalBytes
    });
    await logger.info(`teams export complete: ${totalMessages} messages, ${errors} errors`);

    return {
        chatsTotal: chats.length,
        chatsExported,
        messages: totalMessages,
        bytes: totalBytes,
        errors
    };
}
